import styled from "@emotion/styled";
import { DatePicker, Form, Select, Table, Tag } from "antd";
import { CALL_API } from "common/API";
import COLORS from "common/Colors";
import { MONTH_LIST, STATUS_CODE } from "common/Constants";
import { find, get } from "lodash";
import moment from "moment";
import {
  addDaysinDate,
  addMonthinDate,
  dateInDetail,
  getinitialdate,
  getKenyanDateTime,
  getMonthDateRange,
  getMySqlDate,
  momentDate,
} from "utils/Date";

// Same status ids as leaves: 4 = waiting, 5 = approved, 6 = rejected.
export const ENCASHMENT_STATUS = {
  WAITING: "4",
  APPROVED: "5",
  REJECTED: "6",
};

export const ENCASHMENT_STATUS_LIST = [
  { value: ENCASHMENT_STATUS.WAITING, label: "Waiting for Approval", color: "orange" },
  { value: ENCASHMENT_STATUS.APPROVED, label: "Approved", color: "green" },
  { value: ENCASHMENT_STATUS.REJECTED, label: "Rejected", color: "red" },
];

export const PROCESSING_STATUS_LIST = [
  { value: "Pending", label: "Pending", color: "orange" },
  { value: "Approved", label: "Approved", color: "green" },
];

// onKeyDown for text inputs that take a day count: digits and one decimal point,
// plus editing / navigation keys and Ctrl/Cmd shortcuts (copy, paste, select all).
const ALLOWED_KEYS = ["Backspace", "Delete", "Tab", "Enter", "Escape", "ArrowLeft", "ArrowRight", "Home", "End"];
export const allowNumberKeys = (event) => {
  const { key, ctrlKey, metaKey, currentTarget } = event;
  if (ctrlKey || metaKey || ALLOWED_KEYS.includes(key) || /^[0-9]$/.test(key)) return;
  if (key === "." && !String(currentTarget?.value || "").includes(".")) return;
  event.preventDefault();
};

export const getStatusLabel = (statusId) =>
  ENCASHMENT_STATUS_LIST.find((s) => s.value === String(statusId))?.label || "-";

export const EncashmentStatusTag = ({ statusId }) => {
  const status = ENCASHMENT_STATUS_LIST.find((s) => s.value === String(statusId));
  return status ? <Tag color={status.color}>{status.label}</Tag> : "-";
};

export const ProcessingStatusTag = ({ status }) => {
  const found = PROCESSING_STATUS_LIST.find((s) => s.value === status);
  return status ? <Tag color={found?.color || "default"}>{status}</Tag> : "-";
};

export const formatDate = (date) =>
  date && moment(date).isValid() ? moment(date).format("DD MMM YYYY") : "-";

export const formatDays = (days) =>
  days === undefined || days === null || days === "" ? "-" : `${days} Days`;

// The leave balance API already deducts encashment requests that are still
// waiting for approval. Those days are only really spent once approved, so
// the UI gives them back.
// { [leaveCategoryId]: days } of the user's encashment requests waiting for approval.
export const getPendingEncashmentDays = async (ClientId, userid) => {
  const { code, encashments = [] } = await CALL_API(
    `leave-encashment-list/${ClientId}`,
    "post",
    {
      userid,
      status_id: ENCASHMENT_STATUS.WAITING,
      ...getDateFilterRange({ getFieldsValue: () => ({ duration_type: 4 }) }),
    }
  );
  const pending = {};
  if (code === STATUS_CODE.SUCCESS) {
    encashments.forEach((item) => {
      if (String(item?.status_id) !== ENCASHMENT_STATUS.WAITING) return;
      const id = item?.leave_category_id;
      pending[id] = (pending[id] || 0) + (Number(item?.number_of_days) || 0);
    });
  }
  return pending;
};

// Gives the pending days back to the "available" entry of each leave category
// and takes them out of "consumed", where the API also counts them.
export const addBackPendingEncashment = (response, pending) =>
  Array.isArray(response)
    ? response.map((leave) => {
        const [key] = Object.keys(leave);
        if (!Array.isArray(leave[key])) return leave;
        const days =
          pending[leave[key].find((detail) => detail?.type === "available")?.leaveCategoryId] ||
          0;
        if (!days) return leave;
        return {
          [key]: leave[key].map((detail) => {
            if (detail?.type === "available") {
              return { ...detail, value: Number(detail.value) + days };
            }
            if (detail?.type === "consumed") {
              return { ...detail, value: Math.max(Number(detail.value) - days, 0) };
            }
            return detail;
          }),
        };
      })
    : response;

export const formatAmount =(amount, currency = "KES") =>
  amount === undefined || amount === null || amount === ""
    ? "-"
    : `${currency} ${Number(amount).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })}`;

export const StyledTable = styled(Table)`
  padding-top: 20px;
  table {
    width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
  }
  .ant-table-cell {
    padding: 16px 20px;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
  .clickable-row {
    cursor: pointer;
  }
`;

export const StyledFilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-picker {
    border-radius: 16px !important;
  }
  .search {
    width: 180px;
  }
  .ant-select,
  .ant-picker {
    width: 150px;
  }
  .ant-form-item {
    margin: 0;
  }
  .ant-form-item-explain-connected {
    display: none;
  }
`;

// Date filter shared by the encashment tabs. Works like the Leave tabs:
// duration type 1 = custom start/end, 2 = month, 4 = all (2022 to 3 months ahead).
export const dateFilterInitialValues = (durationType) => ({
  duration_type: durationType,
  month: dateInDetail(getKenyanDateTime()).monthName,
  start_date: momentDate(addMonthinDate(getKenyanDateTime(), -1)),
  end_date: momentDate(addDaysinDate(getKenyanDateTime())),
});

export const getDateFilterRange = (form) => {
  // `true` includes the date fields even before they mount (switching to Custom Date).
  const { duration_type, month, start_date, end_date } = form.getFieldsValue(true);
  const { startDateOfMonth, lastDateOfMonth } = getMonthDateRange(
    dateInDetail(getKenyanDateTime()).year,
    get(find(MONTH_LIST, { value: month }), "index", 0)
  );
  if (duration_type === 2) {
    return {
      start_date: getMySqlDate(startDateOfMonth),
      end_date: getMySqlDate(lastDateOfMonth),
    };
  }
  if (duration_type === 4) {
    return {
      start_date: getMySqlDate(momentDate(addMonthinDate(getinitialdate()))),
      end_date: getMySqlDate(momentDate(addMonthinDate(getKenyanDateTime(), 3))),
    };
  }
  return { start_date: getMySqlDate(start_date), end_date: getMySqlDate(end_date) };
};

const dateRule = () => ({
  // first parameter is needed
  validator(rule, value) {
    return moment(value).isValid() ? Promise.resolve() : Promise.reject();
  },
});

// Renders inline form items only (no wrapper element), so it sits among the
// other filters of a StyledFilterRow.
export const DateFilterForm = ({
  form,
  name,
  durationOptions,
  initialDurationType,
  onChange,
  containerRef,
}) => {
  const validateDateRange = (date) => {
    if (moment(form.getFieldValue("end_date")) < date) {
      form.setFieldsValue({ end_date: date });
    }
  };
  const popup = () => containerRef?.current || document.body;

  return (
    <Form
      form={form}
      name={name}
      component={false}
      initialValues={dateFilterInitialValues(initialDurationType)}
      onValuesChange={() => onChange()}
    >
      <Form.Item name="duration_type" rules={[{ required: true, message: "" }]}>
        <Select options={durationOptions} getPopupContainer={popup} />
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const durationType = getFieldValue("duration_type");
          if (durationType === 1) {
            return (
              <>
                <Form.Item name="start_date" rules={[dateRule]}>
                  <DatePicker
                    placeholder="Start date"
                    format="DD-MM-YYYY"
                    onChange={validateDateRange}
                    allowClear={false}
                    getPopupContainer={popup}
                  />
                </Form.Item>
                <Form.Item name="end_date" rules={[dateRule]}>
                  <DatePicker
                    placeholder="End date"
                    format="DD-MM-YYYY"
                    onChange={validateDateRange}
                    allowClear={false}
                    getPopupContainer={popup}
                  />
                </Form.Item>
              </>
            );
          }
          if (durationType === 2) {
            return (
              <Form.Item name="month" rules={[{ required: true, message: "" }]}>
                <Select options={MONTH_LIST} getPopupContainer={popup} />
              </Form.Item>
            );
          }
          return null;
        }}
      </Form.Item>
    </Form>
  );
};
